import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../api/api";
import { country_url } from "../../api/urls";
import { Controller, useForm } from "react-hook-form";
import {
  TextField,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { MobileNumberDropdown } from "../../components/MobileNumberDropdown";
import { useDispatch } from "react-redux";
import {
  handleAlphaTwoCode,
  handleAuthModal,
  loginUser,
} from "../../Redux/Auth";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import { Grid } from "@mui/material";
import { getMobileNumberLengthFromRegex } from "../../utils/mobileRegexLength";

const buttonStyle = {
  width: "100%",
  padding: "10px",
  backgroundColor: "#000",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  fontSize: "16px",
  marginBottom: "10px",
};

const extractErrorMessage = (error) => {
  let message = "An unexpected error occurred.";
  if (error && error.data) {
    message = error.data || error.data.message || message;
  } else if (error.request) {
    message = "No response from server. Please check your connection.";
  } else if (error.message) {
    message = error.message;
  }
  return message;
};

const Login = ({ onClose, setIsUnRegistered }) => {
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      number: "",
      password: "",
    },
  });
  const [countries, setCountries] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("91");
  const [selectedCountry, setSelectedCountry] = useState(null);
  // console.log(selectedCountry,"i am the console,log")

  const [alphaTwoCode, setAlphaTwoCode] = useState("in");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const { data: countryData } = await api.get(country_url);
        // console.log(countryData,"i am the countrydata here used to be here can ")

        setCountries(countryData);
        if (countryData.length > 0) {
          // this is by default code if nothing works go for this  setSelectedCountryCode(countryData[0]?.country_mobile_code || 91);

          const defaultCountry = countryData.find(
            (c) =>
              c.alpha_two_code.toLowerCase() === "in" ||
              c.country_mobile_code === "91",
          );
          setSelectedCountryCode(defaultCountry?.country_mobile_code || "91");
          // ✅ Add this line to set the full object
          setSelectedCountry(defaultCountry || countryData[0]);
        }
      } catch (err) {
        toast.error(err.message || "Failed to fetch countries");
      }
    };

    fetchCountryCodes();
  }, []);

  useEffect(() => {
    const unsub = watch((value) => {
      let current = value.number;
      if (current?.startsWith("0")) {
        setValue("number", current.slice(1)); // ✅ Strip leading zero immediately
      }
    });
    return () => unsub.unsubscribe();
  }, [watch, setValue]);
  useEffect(() => {
    const unsub = watch((value) => {
      const { primary_mobile, alternate_mobile } = value;

      if (primary_mobile?.startsWith("91")) {
        setValue("primary_mobile", primary_mobile.slice(2));
      } else if (primary_mobile?.startsWith("0")) {
        setValue("primary_mobile", primary_mobile.slice(1));
      }

      if (alternate_mobile?.startsWith("91")) {
        setValue("alternate_mobile", alternate_mobile.slice(2));
      } else if (alternate_mobile?.startsWith("0")) {
        setValue("alternate_mobile", alternate_mobile.slice(1));
      }
    });

    return () => unsub.unsubscribe();
  }, [watch, setValue]);

  // --- insert this after your existing watch useEffects ---
  const numberValue = watch("number");

  useEffect(() => {
    // Only log when something is available so console isn't spammy
    if (!selectedCountry) {
      console.error("Country not selected yet");
      return;
    }

    // Logs for debugging — confirms we get the regex from API and the live input
    // console.log("Selected country:", selectedCountry.name, selectedCountry.alpha_two_code);
    // console.log("Country regex (country_regex):", selectedCountry.country_regex);
    // console.log("Live phone input (number):", numberValue);
  }, [numberValue, selectedCountry]);
  // -------------------------------------------------------

  const handleLogin = useCallback(
    async (data) => {
      if (!selectedCountryCode) {
        toast.error("Please select a valid country code.");
        return;
      }

      setLoading(true);

      try {
        const fullUsername = `${selectedCountryCode}${data.number}`;
        const requestData = {
          country_code: selectedCountryCode,
          username: fullUsername,
          password: data.password,
        };

        // unwrap to get response with token
        const result = await dispatch(loginUser(requestData)).unwrap();

        //  Store token based on Remember Me
        if (data.rememberMe) {
          localStorage.setItem("auth_token", result.token);
        } else {
          sessionStorage.setItem("auth_token", result.token);
        }

        dispatch(handleAlphaTwoCode(alphaTwoCode));
        toast.success("Login successful!");
        // navigate("/");
        // dispatch(updateProfileImage(response.data.profile_image));
        dispatch(handleAuthModal(false));
        reset();
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, reset, selectedCountryCode, alphaTwoCode],
  );

  return (
    <>
      <h2 className="font-bold text-2xl text-center mb-2">Sign-In</h2>
      <form onSubmit={handleSubmit(handleLogin)}>
        <motion.div
          whileFocus={{ scale: 1.02 }}
          style={{ marginBottom: "15px" }}
        >
          <Controller
            name="number"
            control={control}
            rules={{
              required: "Phone number is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Phone Number"
                type="tel" // changed from number to tel
                error={!!error}
                helperText={error ? error.message : ""}
                fullWidth
                name="user_phone"
                autoComplete="tel-national"
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");

                  const mobileMaxLength =
                    getMobileNumberLengthFromRegex(
                      selectedCountry?.country_regex,
                      selectedCountryCode.toString(),
                    ) || 11;

                  if (digitsOnly.length > mobileMaxLength) return; // block extra digits

                  field.onChange(digitsOnly);
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData
                    .getData("text")
                    .replace(/\D/g, "");

                  const mobileMaxLength =
                    getMobileNumberLengthFromRegex(
                      selectedCountry?.country_regex,
                      selectedCountryCode.toString(),
                    ) || 11;

                  e.preventDefault();
                  field.onChange(pasted.slice(0, mobileMaxLength)); // trim if too long
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MobileNumberDropdown
                        countryData={countries}
                        value={selectedCountryCode}
                        onChange={(v) => {
                          setAlphaTwoCode(v.alpha_two_code.toLowerCase());
                          setSelectedCountryCode(v.country_mobile_code);
                          setSelectedCountry(v);

                          setValue("number", "");
                          setValue("password", "");
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </motion.div>

        <motion.div
          whileFocus={{ scale: 1.02 }}
          style={{ marginBottom: "15px" }}
        >
          <Controller
            name="password"
            control={control}
            rules={{ required: "Password is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type={showPassword ? "text" : "password"}
                label={watch("password") ? "" : "Password *"}
                fullWidth
                placeholder="********"
                error={!!error}
                helperText={error ? error.message : ""}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {/* {showPassword ? <EyeOff size={20} /> : <Eye size={20} />} */}
                        {showPassword ? (
                          <Eye size={20} />
                        ) : (
                          <EyeOff size={20} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </motion.div>

        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 1 }}
        >
          {/* <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    {...register("rememberMe")}
                  />
                }
                label={
                  <Typography variant="body2">Remember Me</Typography>
                }
              />
            </Grid> */}
          <Grid item xs="auto">
            <Typography
              variant="body2"
              sx={{
                color: "#000",
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setIsUnRegistered(2)}
            >
              Forgot Password?
            </Typography>
          </Grid>
        </Grid>

        <motion.button
          type="submit"
          disabled={loading}
          whileTap={{ scale: 0.95 }}
          style={{ ...buttonStyle, marginTop: "15px" }}
          whileHover={{ scale: 1.05 }}
        >
          {loading ? "Signing in..." : "Sign-in"}
        </motion.button>

        <motion.div
          // onClick={() => setIsUnRegistered(1)}
          onClick={() => {
            setIsUnRegistered(1);
            window.scrollTo(0, 0); // optional but helps avoid UI scroll shift
          }}
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.05 }}
          className="block w-full text-center mt-[2px] py-3 underline underline-offset-2 text-sm cursor-pointer transition-all duration-300 ease-in-out"
        >
          Don't have an account? Signup
        </motion.div>
      </form>
    </>
  );
};

export default Login;
